import React, { useState, useEffect } from "react";
import { ArrowLeftIcon } from "@radix-ui/react-icons";
import { useNavigate, useParams } from "react-router-dom";
import { JsonData, JsonEditor, githubLightTheme } from "json-edit-react";
import { useMutation, useQuery } from "react-query";
import {
  getRequest,
  optimisticOptions,
  postRequest,
  putRequest,
  queries,
} from "../../../../react-query";
import { validateForm } from "../../../../utils";
import { ScrollArea } from "../../../../components";


interface Activity {
  zones: Record<string, any>;
  parameters: {
    subcategory_mapping: any[];
  };
}

interface FormData {
  name: string;
  search: string;
  activity: Activity;
}

export const UpdateActivity = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [search, setSearch] = useState("");

  const [formData, setFormData] = useState<FormData>({
    name: "",
    search: "",
    activity: {
      zones: {},
      parameters: {
        subcategory_mapping: []
      }
    }
  });



  const validationRules = {
    name: [{ required: true, message: "Activity name is required" }]
  };

  const { mutate: createActivity } = useMutation(
    putRequest,
    optimisticOptions(queries.activities)
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errors = validateForm(formData, validationRules);
    if (Object.keys(errors).length === 0) {
      createActivity({
        endPoint:`/activity/${id}`,
        payload: {
          name: formData.name,
          zones: formData.activity.zones,
          parameters: formData.activity.parameters  
        }
      });
      navigate(-1);
    }
  };


  const onChangeHandler = (name: string, value: any) => {
    setFormData({...formData, [name]: value});
  };

  const { data: activityData = {} }: any = useQuery(
    [queries.activities, id],
    () => getRequest(`/activity/${id}`),
    {
      enabled: !!id,
    }
  );
  const { data: activity = {} } = activityData;


  useEffect(() => {
    if (activity && activity.id) {
      const { name, zones, parameters } = activity
      setFormData({
        ...formData,
        name: name,
        activity: {
          zones: zones,
          parameters: parameters
        }
      });
    }
  }, [activity]);

  return (
    <div className="bg-white w-full h-full p-8 space-y-4">
      <div className="flex w-full h-10 space-x-4 items-center">
        <ArrowLeftIcon
          onClick={() => navigate("/settings")}
          className="w-6 h-6 text-black cursor-pointer"
        />
        <span className="font-bold">Update Activity</span>
      </div>

      <form onSubmit={handleSubmit} className="w-full h-[calc(100vh-210px)] space-y-4 border border-gray-200 py-4 rounded">
        <div className="px-4">
          <ScrollArea className="h-[calc(100vh-230px)] py-4">
            <div className="space-y-6">
              <div className="flex justify-between items-center space-x-4">
                <div className="flex-1">
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                    Activity Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    value={formData.name}
                    onChange={(e) => onChangeHandler("name", e.target.value)}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Search in JSON
                  </label>
                  <input
                    type="text"
                    placeholder="Search..."
                    value={formData.search}
                    onChange={(e) => onChangeHandler("search", e.target.value)}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>

                <JsonEditor
                  data={formData.activity}
                  setData={(value: any) => onChangeHandler("activity", value)}
                  rootName="Activity"
                  enableClipboard={true}
                  theme={githubLightTheme}
                  searchText={formData.search}
                />
              </div>
            </div>
          </ScrollArea>
        </div>

        <div className="w-full flex justify-end px-4">
          <button
            type="submit"
            className="px-6 py-2 text-sm font-medium text-white bg-green rounded-md"
          >
            Submit
          </button>
        </div>
      </form>
    </div>
  );
};
